import { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { Badge, Button, Dialog } from '@/design-system';
import * as dashboardApi from '@/features/dashboard/api/dashboard-api';
import type { TemplateDetail, TemplateSummary } from '@/contracts/templates';

export interface TemplatePreviewDialogProps {
  template: TemplateSummary | null;
  onClose: () => void;
  onUse: (template: TemplateSummary) => void;
}

/**
 * SPEC-010 §26-28/AC-020/AC-021 — a metadata-driven preview: it lists the
 * template's stages/structure, it never mounts the live editor and never
 * executes any URL/webhook/API/AI/automation the template's steps might
 * conceptually represent.
 */
export function TemplatePreviewDialog({ template, onClose, onUse }: TemplatePreviewDialogProps) {
  const [detail, setDetail] = useState<TemplateDetail | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!template) {
      setDetail(null);
      return;
    }
    setLoading(true);
    dashboardApi
      .getTemplateDetail(template.type, template.id)
      .then((res) => setDetail(res.template))
      .finally(() => setLoading(false));
  }, [template]);

  if (!template) return null;

  return (
    <Dialog open={Boolean(template)} onClose={onClose} title={template.name}>
      <div className="flex max-h-[70vh] flex-col gap-4 overflow-y-auto">
        <p className="text-sm text-text-secondary">{template.description}</p>

        {template.tags.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {template.tags.map((tag) => (
              <Badge key={tag}>{tag}</Badge>
            ))}
          </div>
        ) : null}

        <p className="text-xs text-text-muted">
          {template.previewMetadata.nodeCount} etapas · {template.previewMetadata.edgeCount} conexões
        </p>

        {loading ? (
          <p className="text-sm text-text-muted">Carregando prévia…</p>
        ) : detail ? (
          <>
            {detail.useCases.length > 0 ? (
              <div>
                <h4 className="mb-1 text-xs font-semibold uppercase text-text-muted">Casos de uso</h4>
                <ul className="list-inside list-disc text-sm text-text-secondary">
                  {detail.useCases.map((useCase) => (
                    <li key={useCase}>{useCase}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div>
              <h4 className="mb-2 text-xs font-semibold uppercase text-text-muted">Prévia do fluxo</h4>
              <div className="flex flex-wrap items-center gap-1.5" aria-label="Prévia do fluxo do modelo">
                {detail.includedStages.map((stage, index) => (
                  <span key={`${stage}-${index}`} className="flex items-center gap-1.5">
                    <span className="rounded-md border border-border bg-surface-2 px-2 py-1 text-xs text-text-secondary">
                      {stage}
                    </span>
                    {index < detail.includedStages.length - 1 ? (
                      <ArrowRight size={12} className="shrink-0 text-text-muted" />
                    ) : null}
                  </span>
                ))}
              </div>
            </div>
          </>
        ) : null}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Fechar
          </Button>
          <Button type="button" onClick={() => onUse(template)}>
            Usar modelo
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
